import { useMemo } from 'react'
import { DailyTotals } from '@/types'
import type { WeeklyPlanVsFactRow } from '@/hooks/useWeeklyPlanVsFact'
import type { PlanData } from '@/hooks/useStudioAnalytics'
import { calculateAverageSale, calculatePlanDeviation } from '@/lib/analytics/metricsCalculations'

export interface SalesMixRange {
  mode: 'period' | 'week' | 'last_3_days'
  start: string
  end: string
}

interface SalesMixTotals {
  factRevenue: number
  factCount: number
  planRevenue: number
  planCount: number
  planAvgCheck: number
  totalRevenue: number
  shareInRevenuePct: number
  avgCheck: number
  deviationToYesterdayPct: number
}

export interface SalesMixBreakdownItem {
  key: string
  label: string
  factRevenue: number
  planRevenue: number
  factCount: number
  planCount: number
  factAvgCheck: number
  planAvgCheck: number
}

export type RecurringSalesDetailMode = 'regular' | 'subscription'

export interface RecurringSalesDetailItem {
  key: string
  mode: RecurringSalesDetailMode
  title: string
  factRevenue: number
  planRevenue: number
  factCount: number
  planCount: number
  factAvgCheck: number
  planAvgCheck: number
  revenueDeviationPct: number
}

export interface SalesMixAnalyticsResult {
  recurringClientsAnalytics: SalesMixTotals
  newSalesAnalytics: SalesMixTotals
  autoRenewalsAnalytics: SalesMixTotals
  recurringSalesBreakdown: SalesMixBreakdownItem[]
  newSalesBreakdown: SalesMixBreakdownItem[]
  recurringSalesDetails: Record<RecurringSalesDetailMode, RecurringSalesDetailItem[]>
  refundsAmount: number
  recurringShareWithoutRefundsPct: number
  newSalesShareWithoutRefundsPct: number
  autoRenewalsShareWithoutRefundsPct: number
  refundsSharePct: number
}

interface UseSalesMixAnalyticsParams {
  selectedRange: SalesMixRange | null
  selectedDailyData: DailyTotals[]
  selectedWeekPlanRow: WeeklyPlanVsFactRow | null
  planData: PlanData | null
}

const EMPTY_TOTALS: SalesMixTotals = {
  factRevenue: 0,
  factCount: 0,
  planRevenue: 0,
  planCount: 0,
  planAvgCheck: 0,
  totalRevenue: 0,
  shareInRevenuePct: 0,
  avgCheck: 0,
  deviationToYesterdayPct: 0
}

function toNumber(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function calculatePlanAvgCheck(
  planRevenue: number,
  planCount: number,
  storedAvgCheck: number,
  shouldUseStoredAvgCheck: boolean
): number {
  if (shouldUseStoredAvgCheck && storedAvgCheck > 0) {
    return storedAvgCheck
  }

  return planCount > 0 ? planRevenue / planCount : 0
}

function toDateOnly(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function countDaysInclusive(start: string, end: string): number {
  const startDate = new Date(start)
  const endDate = new Date(end)
  const diff = endDate.getTime() - startDate.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1
}

function getYesterdayDate(): string {
  const now = new Date()
  now.setDate(now.getDate() - 1)
  return toDateOnly(now)
}

interface FactMetricSource {
  selectedRange: SalesMixRange | null
  selectedDailyData: DailyTotals[]
  selectedWeekPlanRow: WeeklyPlanVsFactRow | null
  planData: PlanData | null
}

interface FactMetricConfig {
  weekField: keyof WeeklyPlanVsFactRow
  periodField: keyof PlanData
  dailyField: keyof DailyTotals
}

function getFactMetricValue(
  source: FactMetricSource,
  config: FactMetricConfig
): number {
  if (source.selectedRange?.mode === 'week' && source.selectedWeekPlanRow) {
    return toNumber(source.selectedWeekPlanRow[config.weekField] as number | null | undefined)
  }

  if (source.planData && source.planData[config.periodField] != null) {
    return toNumber(source.planData[config.periodField] as number | null | undefined)
  }

  return source.selectedDailyData.reduce(
    (sum, day) => sum + toNumber(day[config.dailyField] as number | null | undefined),
    0
  )
}

function buildBreakdownItem(
  key: string,
  label: string,
  factRevenue: number,
  factCount: number,
  planRevenue: number,
  planCount: number
): SalesMixBreakdownItem {
  return {
    key,
    label,
    factRevenue,
    planRevenue,
    factCount,
    planCount,
    factAvgCheck: factCount > 0 ? factRevenue / factCount : 0,
    planAvgCheck: planCount > 0 ? planRevenue / planCount : 0
  }
}

const RECURRING_DETAIL_CONFIGS = {
  regular: [
    {
      key: 'renewals',
      title: 'Продления абонементов',
      factCountField: 'renewals_count',
      factAmountField: 'renewals_amount',
      weekFactCountField: 'weekly_renewals_count_fact',
      weekFactAmountField: 'weekly_renewals_amount_fact',
      weekPlanCountField: 'weekly_renewals_count_plan',
      weekPlanAmountField: 'weekly_renewals_amount_plan',
      periodFactCountField: 'renewals_count_fact',
      periodFactAmountField: 'renewals_amount_fact',
      periodPlanCountField: 'renewals_count_plan',
      periodPlanAmountField: 'renewals_amount_plan'
    },
    {
      key: 'upgrades',
      title: 'Апгрейды абонементов',
      factCountField: 'upgrades_count',
      factAmountField: 'upgrades_amount',
      weekFactCountField: 'weekly_upgrades_count_fact',
      weekFactAmountField: 'weekly_upgrades_amount_fact',
      weekPlanCountField: 'weekly_upgrades_count_plan',
      weekPlanAmountField: 'weekly_upgrades_amount_plan',
      periodFactCountField: 'upgrades_count_fact',
      periodFactAmountField: 'upgrades_amount_fact',
      periodPlanCountField: 'upgrades_count_plan',
      periodPlanAmountField: 'upgrades_amount_plan'
    },
    {
      key: 'returning_clients',
      title: 'Продажи абонементов БЧС',
      factCountField: 'returning_clients_count',
      factAmountField: 'returning_clients_amount',
      weekFactCountField: 'weekly_returning_clients_count_fact',
      weekFactAmountField: 'weekly_returning_clients_amount_fact',
      weekPlanCountField: 'weekly_returning_clients_count_plan',
      weekPlanAmountField: 'weekly_returning_clients_amount_plan',
      periodFactCountField: 'returning_clients_count_fact',
      periodFactAmountField: 'returning_clients_amount_fact',
      periodPlanCountField: 'returning_clients_count_plan',
      periodPlanAmountField: 'returning_clients_amount_plan'
    }
  ],
  subscription: [
    {
      key: 'subscription_renewals',
      title: 'Автопродления подписок',
      factCountField: 'subscription_renewals_count',
      factAmountField: 'subscription_renewals_amount',
      weekFactCountField: 'weekly_subscription_renewals_count_fact',
      weekFactAmountField: 'weekly_subscription_renewals_amount_fact',
      weekPlanCountField: 'weekly_subscription_renewals_count_plan',
      weekPlanAmountField: 'weekly_subscription_renewals_amount_plan',
      periodFactCountField: 'subscription_renewals_count_fact',
      periodFactAmountField: 'subscription_renewals_amount_fact',
      periodPlanCountField: 'subscription_renewals_count_plan',
      periodPlanAmountField: 'subscription_renewals_amount_plan'
    },
    {
      key: 'subscription_upgrades',
      title: 'Переходы с абонемента на подписку',
      factCountField: 'subscription_upgrades_count',
      factAmountField: 'subscription_upgrades_amount',
      weekFactCountField: 'weekly_subscription_upgrades_count_fact',
      weekFactAmountField: 'weekly_subscription_upgrades_amount_fact',
      weekPlanCountField: 'weekly_subscription_upgrades_count_plan',
      weekPlanAmountField: 'weekly_subscription_upgrades_amount_plan',
      periodFactCountField: 'subscription_upgrades_count_fact',
      periodFactAmountField: 'subscription_upgrades_amount_fact',
      periodPlanCountField: 'subscription_upgrades_count_plan',
      periodPlanAmountField: 'subscription_upgrades_amount_plan'
    },
    {
      key: 'subscription_returning_clients',
      title: 'Продажи подписок БЧС',
      factCountField: 'subscription_returning_clients_count',
      factAmountField: 'subscription_returning_clients_amount',
      weekFactCountField: 'weekly_subscription_returning_clients_count_fact',
      weekFactAmountField: 'weekly_subscription_returning_clients_amount_fact',
      weekPlanCountField: 'weekly_subscription_returning_clients_count_plan',
      weekPlanAmountField: 'weekly_subscription_returning_clients_amount_plan',
      periodFactCountField: 'subscription_returning_clients_count_fact',
      periodFactAmountField: 'subscription_returning_clients_amount_fact',
      periodPlanCountField: 'subscription_returning_clients_count_plan',
      periodPlanAmountField: 'subscription_returning_clients_amount_plan'
    }
  ]
} as const

export function useSalesMixAnalytics({
  selectedRange,
  selectedDailyData,
  selectedWeekPlanRow,
  planData
}: UseSalesMixAnalyticsParams): SalesMixAnalyticsResult {
  const factSource: FactMetricSource = {
    selectedRange,
    selectedDailyData,
    selectedWeekPlanRow,
    planData
  }

  const recurringClientsAnalytics = useMemo<SalesMixTotals>(() => {
    if (!selectedRange) return EMPTY_TOTALS

    const rangeStart = selectedRange.start
    const rangeEnd = selectedRange.end

    const factRevenue =
      getFactMetricValue(factSource, { weekField: 'weekly_renewals_amount_fact', periodField: 'renewals_amount_fact', dailyField: 'renewals_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_upgrades_amount_fact', periodField: 'upgrades_amount_fact', dailyField: 'upgrades_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_returning_clients_amount_fact', periodField: 'returning_clients_amount_fact', dailyField: 'returning_clients_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_upgrades_amount_fact', periodField: 'subscription_upgrades_amount_fact', dailyField: 'subscription_upgrades_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_returning_clients_amount_fact', periodField: 'subscription_returning_clients_amount_fact', dailyField: 'subscription_returning_clients_amount' }) +
      getFactMetricValue(factSource, {
        weekField: 'weekly_membership_fees_subscription_upgrades_amount_fact',
        periodField: 'membership_fees_subscription_upgrades_amount_fact',
        dailyField: 'membership_fees_subscription_upgrades_amount'
      }) +
      getFactMetricValue(factSource, {
        weekField: 'weekly_membership_fees_subscription_returning_amount_fact',
        periodField: 'membership_fees_subscription_returning_amount_fact',
        dailyField: 'membership_fees_subscription_returning_amount'
      })

    const factCount =
      getFactMetricValue(factSource, { weekField: 'weekly_renewals_count_fact', periodField: 'renewals_count_fact', dailyField: 'renewals_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_upgrades_count_fact', periodField: 'upgrades_count_fact', dailyField: 'upgrades_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_returning_clients_count_fact', periodField: 'returning_clients_count_fact', dailyField: 'returning_clients_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_upgrades_count_fact', periodField: 'subscription_upgrades_count_fact', dailyField: 'subscription_upgrades_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_returning_clients_count_fact', periodField: 'subscription_returning_clients_count_fact', dailyField: 'subscription_returning_clients_count' })

    const planRevenue = selectedRange.mode === 'week'
      ? (selectedWeekPlanRow?.weekly_renewals_amount_plan || 0)
        + (selectedWeekPlanRow?.weekly_upgrades_amount_plan || 0)
        + (selectedWeekPlanRow?.weekly_returning_clients_amount_plan || 0)
        + (selectedWeekPlanRow?.weekly_subscription_upgrades_amount_plan || 0)
        + (selectedWeekPlanRow?.weekly_subscription_returning_clients_amount_plan || 0)
        + ((selectedWeekPlanRow as any)?.weekly_membership_fees_subscription_upgrades_plan || 0)
        + ((selectedWeekPlanRow as any)?.weekly_membership_fees_subscription_returning_plan || 0)
      : (planData?.renewals_amount_plan || 0)
        + (planData?.upgrades_amount_plan || 0)
        + (planData?.returning_clients_amount_plan || 0)
        + ((planData as any)?.subscription_upgrades_amount_plan || 0)
        + ((planData as any)?.subscription_returning_clients_amount_plan || 0)
        + ((planData as any)?.membership_fees_subscription_upgrades_plan || 0)
        + ((planData as any)?.membership_fees_subscription_returning_plan || 0)

    const renewalsPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_renewals_count_plan)
      : toNumber((planData as any)?.renewals_count_plan)
    const upgradesPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_upgrades_count_plan)
      : toNumber((planData as any)?.upgrades_count_plan)
    const returningClientsPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_returning_clients_count_plan)
      : toNumber((planData as any)?.returning_clients_count_plan)
    const regularPlanCount = renewalsPlanCount + upgradesPlanCount + returningClientsPlanCount
    const subscriptionPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_subscription_upgrades_count_plan)
        + toNumber(selectedWeekPlanRow?.weekly_subscription_returning_clients_count_plan)
      : toNumber((planData as any)?.subscription_upgrades_count_plan)
        + toNumber((planData as any)?.subscription_returning_clients_count_plan)
    const membershipFeesUpgradesPlan = selectedRange.mode === 'week'
      ? toNumber((selectedWeekPlanRow as any)?.weekly_membership_fees_subscription_upgrades_plan)
      : toNumber((planData as any)?.membership_fees_subscription_upgrades_plan)
    const membershipFeesReturningPlan = selectedRange.mode === 'week'
      ? toNumber((selectedWeekPlanRow as any)?.weekly_membership_fees_subscription_returning_plan)
      : toNumber((planData as any)?.membership_fees_subscription_returning_plan)
    const membershipFeesUpgradesFact = getFactMetricValue(factSource, {
      weekField: 'weekly_membership_fees_subscription_upgrades_amount_fact',
      periodField: 'membership_fees_subscription_upgrades_amount_fact',
      dailyField: 'membership_fees_subscription_upgrades_amount'
    })
    const membershipFeesReturningFact = getFactMetricValue(factSource, {
      weekField: 'weekly_membership_fees_subscription_returning_amount_fact',
      periodField: 'membership_fees_subscription_returning_amount_fact',
      dailyField: 'membership_fees_subscription_returning_amount'
    })
    const planRevenueWithoutMembershipFees = planRevenue - membershipFeesUpgradesPlan - membershipFeesReturningPlan
    const factRevenueWithoutMembershipFees = factRevenue - membershipFeesUpgradesFact - membershipFeesReturningFact
    const planCount = regularPlanCount + subscriptionPlanCount
    const activeRegularAovPlans = [
      { count: renewalsPlanCount, avgCheck: toNumber(selectedWeekPlanRow?.weekly_renewals_aov_plan) },
      { count: upgradesPlanCount, avgCheck: toNumber(selectedWeekPlanRow?.weekly_upgrades_aov_plan) },
      { count: returningClientsPlanCount, avgCheck: toNumber(selectedWeekPlanRow?.weekly_returning_clients_aov_plan) }
    ].filter((item) => item.count > 0)
    const storedRegularAvgCheck = selectedRange.mode === 'week'
      ? toNumber((selectedWeekPlanRow as any)?.weekly_returning_sales_aov_plan) ||
        (activeRegularAovPlans.length === 1 ? activeRegularAovPlans[0].avgCheck : 0)
      : toNumber((planData as any)?.avg_recurring_sales_check_plan)
    const shouldUseStoredRegularAvgCheck =
      regularPlanCount > 0 &&
      subscriptionPlanCount === 0 &&
      (membershipFeesUpgradesPlan + membershipFeesReturningPlan) === 0 &&
      selectedWeekPlanRow?.studio_internal_id !== 0
    const planAvgCheck = calculatePlanAvgCheck(
      planRevenueWithoutMembershipFees,
      planCount,
      storedRegularAvgCheck,
      shouldUseStoredRegularAvgCheck
    )

    const totalRevenue = selectedDailyData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0)

    const totalRangeDays = countDaysInclusive(rangeStart, rangeEnd)
    const yesterday = getYesterdayDate()
    const cappedYesterday = yesterday > rangeEnd ? rangeEnd : yesterday
    const elapsedDaysToYesterday = cappedYesterday >= rangeStart
      ? countDaysInclusive(rangeStart, cappedYesterday)
      : 0
    const planToYesterday = totalRangeDays > 0 && elapsedDaysToYesterday > 0
      ? planRevenue * (elapsedDaysToYesterday / totalRangeDays)
      : 0
    const factToYesterday = elapsedDaysToYesterday > 0
      ? selectedDailyData
        .filter((day) => day.metric_date >= rangeStart && day.metric_date <= cappedYesterday)
        .reduce((sum, day) => (
          sum
          + (day.renewals_amount || 0)
          + (day.upgrades_amount || 0)
          + (day.returning_clients_amount || 0)
          + (day.subscription_upgrades_amount || 0)
          + (day.subscription_returning_clients_amount || 0)
          + (day.membership_fees_subscription_upgrades_amount || 0)
          + (day.membership_fees_subscription_returning_amount || 0)
        ), 0)
      : 0
    const deviationToYesterdayPct = elapsedDaysToYesterday <= 1
      ? 0
      : planToYesterday > 0
        ? ((factToYesterday - planToYesterday) / planToYesterday) * 100
        : 0

    return {
      factRevenue,
      factCount,
      planRevenue,
      planCount,
      planAvgCheck,
      totalRevenue,
      shareInRevenuePct: totalRevenue > 0 ? (factRevenue / totalRevenue) * 100 : 0,
      avgCheck: factCount > 0 ? factRevenueWithoutMembershipFees / factCount : 0,
      deviationToYesterdayPct
    }
  }, [selectedRange, selectedDailyData, selectedWeekPlanRow, planData])

  const autoRenewalsAnalytics = useMemo<SalesMixTotals>(() => {
    if (!selectedRange) return EMPTY_TOTALS

    const rangeStart = selectedRange.start
    const rangeEnd = selectedRange.end

    const factRevenue = getFactMetricValue(factSource, {
      weekField: 'weekly_subscription_renewals_amount_fact',
      periodField: 'subscription_renewals_amount_fact',
      dailyField: 'subscription_renewals_amount'
    })
    const factCount = getFactMetricValue(factSource, {
      weekField: 'weekly_subscription_renewals_count_fact',
      periodField: 'subscription_renewals_count_fact',
      dailyField: 'subscription_renewals_count'
    })
    const planRevenue = selectedRange.mode === 'week'
      ? toNumber((selectedWeekPlanRow as any)?.weekly_subscription_renewals_amount_plan)
      : toNumber((planData as any)?.subscription_renewals_amount_plan)
    const planCount = selectedRange.mode === 'week'
      ? toNumber((selectedWeekPlanRow as any)?.weekly_subscription_renewals_count_plan)
      : toNumber((planData as any)?.subscription_renewals_count_plan)
    const planAvgCheck = planCount > 0 ? planRevenue / planCount : 0

    const totalRevenue = selectedDailyData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0)

    const totalRangeDays = countDaysInclusive(rangeStart, rangeEnd)
    const yesterday = getYesterdayDate()
    const cappedYesterday = yesterday > rangeEnd ? rangeEnd : yesterday
    const elapsedDaysToYesterday = cappedYesterday >= rangeStart
      ? countDaysInclusive(rangeStart, cappedYesterday)
      : 0
    const planToYesterday = totalRangeDays > 0 && elapsedDaysToYesterday > 0
      ? planRevenue * (elapsedDaysToYesterday / totalRangeDays)
      : 0
    const factToYesterday = elapsedDaysToYesterday > 0
      ? selectedDailyData
        .filter((day) => day.metric_date >= rangeStart && day.metric_date <= cappedYesterday)
        .reduce((sum, day) => sum + (day.subscription_renewals_amount || 0), 0)
      : 0
    const deviationToYesterdayPct = elapsedDaysToYesterday <= 1
      ? 0
      : planToYesterday > 0
        ? ((factToYesterday - planToYesterday) / planToYesterday) * 100
        : 0

    return {
      factRevenue,
      factCount,
      planRevenue,
      planCount,
      planAvgCheck,
      totalRevenue,
      shareInRevenuePct: totalRevenue > 0 ? (factRevenue / totalRevenue) * 100 : 0,
      avgCheck: factCount > 0 ? factRevenue / factCount : 0,
      deviationToYesterdayPct
    }
  }, [selectedRange, selectedDailyData, selectedWeekPlanRow, planData])

  const recurringSalesBreakdown = useMemo<SalesMixBreakdownItem[]>(() => {
    if (!selectedRange) return []

    const regularFactRevenue =
      getFactMetricValue(factSource, { weekField: 'weekly_renewals_amount_fact', periodField: 'renewals_amount_fact', dailyField: 'renewals_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_upgrades_amount_fact', periodField: 'upgrades_amount_fact', dailyField: 'upgrades_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_returning_clients_amount_fact', periodField: 'returning_clients_amount_fact', dailyField: 'returning_clients_amount' })
    const regularFactCount =
      getFactMetricValue(factSource, { weekField: 'weekly_renewals_count_fact', periodField: 'renewals_count_fact', dailyField: 'renewals_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_upgrades_count_fact', periodField: 'upgrades_count_fact', dailyField: 'upgrades_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_returning_clients_count_fact', periodField: 'returning_clients_count_fact', dailyField: 'returning_clients_count' })
    const regularPlanRevenue = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_renewals_amount_plan)
        + toNumber(selectedWeekPlanRow?.weekly_upgrades_amount_plan)
        + toNumber(selectedWeekPlanRow?.weekly_returning_clients_amount_plan)
      : toNumber(planData?.renewals_amount_plan)
        + toNumber(planData?.upgrades_amount_plan)
        + toNumber(planData?.returning_clients_amount_plan)
    const regularPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_renewals_count_plan)
        + toNumber(selectedWeekPlanRow?.weekly_upgrades_count_plan)
        + toNumber(selectedWeekPlanRow?.weekly_returning_clients_count_plan)
      : toNumber((planData as any)?.renewals_count_plan)
        + toNumber((planData as any)?.upgrades_count_plan)
        + toNumber((planData as any)?.returning_clients_count_plan)

    const subscriptionFactRevenue =
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_upgrades_amount_fact', periodField: 'subscription_upgrades_amount_fact', dailyField: 'subscription_upgrades_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_returning_clients_amount_fact', periodField: 'subscription_returning_clients_amount_fact', dailyField: 'subscription_returning_clients_amount' })
    const subscriptionFactCount =
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_upgrades_count_fact', periodField: 'subscription_upgrades_count_fact', dailyField: 'subscription_upgrades_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_subscription_returning_clients_count_fact', periodField: 'subscription_returning_clients_count_fact', dailyField: 'subscription_returning_clients_count' })
    const subscriptionPlanRevenue = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_subscription_upgrades_amount_plan)
        + toNumber(selectedWeekPlanRow?.weekly_subscription_returning_clients_amount_plan)
      : toNumber((planData as any)?.subscription_upgrades_amount_plan)
        + toNumber((planData as any)?.subscription_returning_clients_amount_plan)
    const subscriptionPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_subscription_upgrades_count_plan)
        + toNumber(selectedWeekPlanRow?.weekly_subscription_returning_clients_count_plan)
      : toNumber((planData as any)?.subscription_upgrades_count_plan)
        + toNumber((planData as any)?.subscription_returning_clients_count_plan)

    return [
      buildBreakdownItem('regular', 'Абонементы', regularFactRevenue, regularFactCount, regularPlanRevenue, regularPlanCount),
      buildBreakdownItem('subscription', 'Подписки', subscriptionFactRevenue, subscriptionFactCount, subscriptionPlanRevenue, subscriptionPlanCount)
    ]
  }, [selectedRange, selectedDailyData, selectedWeekPlanRow, planData])

  const recurringSalesDetails = useMemo<Record<RecurringSalesDetailMode, RecurringSalesDetailItem[]>>(() => {
    if (!selectedRange) {
      return { regular: [], subscription: [] }
    }

    const buildItems = (mode: RecurringSalesDetailMode): RecurringSalesDetailItem[] => {
      return RECURRING_DETAIL_CONFIGS[mode].map((config) => {
        const factRevenue = getFactMetricValue(factSource, {
          weekField: config.weekFactAmountField,
          periodField: config.periodFactAmountField,
          dailyField: config.factAmountField
        })
        const factCount = getFactMetricValue(factSource, {
          weekField: config.weekFactCountField,
          periodField: config.periodFactCountField,
          dailyField: config.factCountField
        })
        const planRevenue = selectedRange.mode === 'week'
          ? toNumber((selectedWeekPlanRow as any)?.[config.weekPlanAmountField])
          : toNumber((planData as any)?.[config.periodPlanAmountField])
        const planCount = selectedRange.mode === 'week'
          ? toNumber((selectedWeekPlanRow as any)?.[config.weekPlanCountField])
          : toNumber((planData as any)?.[config.periodPlanCountField])

        return {
          key: config.key,
          mode,
          title: config.title,
          factRevenue,
          planRevenue,
          factCount,
          planCount,
          factAvgCheck: calculateAverageSale(factRevenue, factCount),
          planAvgCheck: calculateAverageSale(planRevenue, planCount),
          revenueDeviationPct: calculatePlanDeviation(factRevenue, planRevenue)
        }
      })
    }

    return {
      regular: buildItems('regular'),
      subscription: buildItems('subscription')
    }
  }, [selectedRange, selectedDailyData, selectedWeekPlanRow, planData])

  const newSalesAnalytics = useMemo<SalesMixTotals>(() => {
    if (!selectedRange) return EMPTY_TOTALS

    const rangeStart = selectedRange.start
    const rangeEnd = selectedRange.end

    const factRevenue =
      getFactMetricValue(factSource, { weekField: 'weekly_new_sales_amount_fact', periodField: 'new_sales_amount_fact', dailyField: 'new_sales_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_new_subscriptions_amount_fact', periodField: 'new_subscriptions_amount_fact', dailyField: 'new_subscriptions_amount' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_membership_fees_new_amount_fact', periodField: 'membership_fees_new_amount_fact', dailyField: 'membership_fees_new_amount' })
    const factCount =
      getFactMetricValue(factSource, { weekField: 'weekly_new_sales_fact', periodField: 'new_sales_count_fact', dailyField: 'new_sales_count' }) +
      getFactMetricValue(factSource, { weekField: 'weekly_new_subscriptions_count_fact', periodField: 'new_subscriptions_count_fact', dailyField: 'new_subscriptions_count' })

    const planRevenue = selectedRange.mode === 'week'
      ? (selectedWeekPlanRow?.weekly_new_sales_amount_plan || 0)
        + (selectedWeekPlanRow?.weekly_new_subscriptions_amount_plan || 0)
        + (selectedWeekPlanRow?.weekly_membership_fees_new_plan || 0)
      : (planData?.new_sales_amount_plan || 0)
        + (planData?.new_subscriptions_amount_plan || 0)
        + (planData?.membership_fees_new_plan || 0)
    const regularPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_new_sales_plan)
      : toNumber(planData?.new_sales_count_plan)
    const subscriptionPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_new_subscriptions_count_plan)
      : toNumber(planData?.new_subscriptions_count_plan)
    const membershipFeesNewPlan = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_membership_fees_new_plan)
      : toNumber(planData?.membership_fees_new_plan)
    const membershipFeesNewFact = getFactMetricValue(factSource, {
      weekField: 'weekly_membership_fees_new_amount_fact',
      periodField: 'membership_fees_new_amount_fact',
      dailyField: 'membership_fees_new_amount'
    })
    const planRevenueWithoutMembershipFees = planRevenue - membershipFeesNewPlan
    const factRevenueWithoutMembershipFees = factRevenue - membershipFeesNewFact
    const planCount = regularPlanCount + subscriptionPlanCount
    const storedAvgCheck = regularPlanCount > 0
      ? toNumber(
          selectedRange.mode === 'week'
            ? selectedWeekPlanRow?.weekly_new_sales_aov_plan
            : planData?.avg_new_sales_check_plan
        )
      : toNumber(
          selectedRange.mode === 'week'
            ? selectedWeekPlanRow?.weekly_new_subscriptions_aov_plan
            : planData?.avg_new_subscription_check_plan
        )
    const shouldUseStoredAvgCheck =
      selectedWeekPlanRow?.studio_internal_id !== 0 &&
      ((regularPlanCount > 0 && subscriptionPlanCount === 0) ||
        (subscriptionPlanCount > 0 && regularPlanCount === 0)) &&
      membershipFeesNewPlan === 0
    const planAvgCheck = calculatePlanAvgCheck(
      planRevenueWithoutMembershipFees,
      planCount,
      storedAvgCheck,
      shouldUseStoredAvgCheck
    )

    const totalRevenue = selectedDailyData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0)
    const totalRangeDays = countDaysInclusive(rangeStart, rangeEnd)
    const yesterday = getYesterdayDate()
    const cappedYesterday = yesterday > rangeEnd ? rangeEnd : yesterday
    const elapsedDaysToYesterday = cappedYesterday >= rangeStart
      ? countDaysInclusive(rangeStart, cappedYesterday)
      : 0
    const planToYesterday = totalRangeDays > 0 && elapsedDaysToYesterday > 0
      ? planRevenue * (elapsedDaysToYesterday / totalRangeDays)
      : 0
    const factToYesterday = elapsedDaysToYesterday > 0
      ? selectedDailyData
        .filter((day) => day.metric_date >= rangeStart && day.metric_date <= cappedYesterday)
        .reduce((sum, day) => (
          sum
          + (day.new_sales_amount || 0)
          + (day.new_subscriptions_amount || 0)
          + (day.membership_fees_new_amount || 0)
        ), 0)
      : 0
    const deviationToYesterdayPct = elapsedDaysToYesterday <= 1
      ? 0
      : planToYesterday > 0
        ? ((factToYesterday - planToYesterday) / planToYesterday) * 100
        : 0

    return {
      factRevenue,
      factCount,
      planRevenue,
      planCount,
      planAvgCheck,
      totalRevenue,
      shareInRevenuePct: totalRevenue > 0 ? (factRevenue / totalRevenue) * 100 : 0,
      avgCheck: factCount > 0 ? factRevenueWithoutMembershipFees / factCount : 0,
      deviationToYesterdayPct
    }
  }, [selectedRange, selectedDailyData, selectedWeekPlanRow, planData])

  const newSalesBreakdown = useMemo<SalesMixBreakdownItem[]>(() => {
    if (!selectedRange) return []

    const regularFactRevenue = getFactMetricValue(factSource, {
      weekField: 'weekly_new_sales_amount_fact',
      periodField: 'new_sales_amount_fact',
      dailyField: 'new_sales_amount'
    })
    const regularFactCount = getFactMetricValue(factSource, {
      weekField: 'weekly_new_sales_fact',
      periodField: 'new_sales_count_fact',
      dailyField: 'new_sales_count'
    })
    const regularPlanRevenue = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_new_sales_amount_plan)
      : toNumber(planData?.new_sales_amount_plan)
    const regularPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_new_sales_plan)
      : toNumber(planData?.new_sales_count_plan)

    const subscriptionFactRevenue = getFactMetricValue(factSource, {
      weekField: 'weekly_new_subscriptions_amount_fact',
      periodField: 'new_subscriptions_amount_fact',
      dailyField: 'new_subscriptions_amount'
    })
    const subscriptionFactCount = getFactMetricValue(factSource, {
      weekField: 'weekly_new_subscriptions_count_fact',
      periodField: 'new_subscriptions_count_fact',
      dailyField: 'new_subscriptions_count'
    })
    const subscriptionPlanRevenue = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_new_subscriptions_amount_plan)
      : toNumber(planData?.new_subscriptions_amount_plan)
    const subscriptionPlanCount = selectedRange.mode === 'week'
      ? toNumber(selectedWeekPlanRow?.weekly_new_subscriptions_count_plan)
      : toNumber(planData?.new_subscriptions_count_plan)

    return [
      buildBreakdownItem('regular', 'Абонементы', regularFactRevenue, regularFactCount, regularPlanRevenue, regularPlanCount),
      buildBreakdownItem('subscription', 'Подписки', subscriptionFactRevenue, subscriptionFactCount, subscriptionPlanRevenue, subscriptionPlanCount)
    ]
  }, [selectedRange, selectedDailyData, selectedWeekPlanRow, planData])

  const refundsAmount = useMemo(
    () => selectedDailyData.reduce((sum, day) => sum + (day.refunds_amount || 0), 0),
    [selectedDailyData]
  )

  const revenueWithoutRefundsBase = Math.max(
    0,
    recurringClientsAnalytics.factRevenue + newSalesAnalytics.factRevenue + autoRenewalsAnalytics.factRevenue
  )

  const recurringShareWithoutRefundsPct = revenueWithoutRefundsBase > 0
    ? (recurringClientsAnalytics.factRevenue / revenueWithoutRefundsBase) * 100
    : 0
  const newSalesShareWithoutRefundsPct = revenueWithoutRefundsBase > 0
    ? (newSalesAnalytics.factRevenue / revenueWithoutRefundsBase) * 100
    : 0
  const autoRenewalsShareWithoutRefundsPct = revenueWithoutRefundsBase > 0
    ? (autoRenewalsAnalytics.factRevenue / revenueWithoutRefundsBase) * 100
    : 0
  const refundsSharePct = revenueWithoutRefundsBase > 0
    ? Math.abs(refundsAmount / revenueWithoutRefundsBase) * 100
    : 0

  return {
    recurringClientsAnalytics,
    newSalesAnalytics,
    autoRenewalsAnalytics,
    recurringSalesBreakdown,
    newSalesBreakdown,
    recurringSalesDetails,
    refundsAmount,
    recurringShareWithoutRefundsPct,
    newSalesShareWithoutRefundsPct,
    autoRenewalsShareWithoutRefundsPct,
    refundsSharePct
  }
}
